import React, { useMemo, useRef, forwardRef, useCallback } from 'react'
import styled from 'styled-components'
import { renderCssStringOfRow, renderCssStringOfContainer } from './helper'
import LayoutContext, { ILayoutProps } from './LayoutContext'
import { alignValues } from './constants'
import { TGutterValues, TResponsiveGutter, TAlign, TJustify, TContainer } from './types'

export interface IRowProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
    gutter?: TGutterValues | TResponsiveGutter
    align?: TAlign
    justify?: TJustify
    children: React.ReactNode
    container?: TContainer
}

const RowLayout = styled.div<{
    cssString: string
    align: TAlign
    justify: TJustify
}>`
    box-sizing: border-box;
    display: flex;
    flex-flow: row wrap;
    align-items: ${({ align }) => alignValues[align]};
    justify-content: ${({ justify }) => justify};
    ${({ cssString }) => cssString};
`

const ContainerLayout = styled.div<{
    containerCssString: string
}>`
    ${({ containerCssString }) => containerCssString}
`

const Row = forwardRef<HTMLDivElement, IRowProps>(
    ({ gutter = 0, align = 'top', justify = 'start', container, children, ...restProps }, ref) => {
        const rowCssString = useMemo(() => renderCssStringOfRow(gutter), [gutter])
        const containerCssString = useMemo(() => renderCssStringOfContainer(container), [container])
        const props = useRef<ILayoutProps>({
            gutter
        })
        const renderContainerLayout = useCallback(
            (children: JSX.Element): JSX.Element => {
                if (container) {
                    return (
                        <ContainerLayout className='vnwLayout__container' containerCssString={containerCssString}>
                            {children}
                        </ContainerLayout>
                    )
                }
                return children
            },
            [container, containerCssString]
        )
        return renderContainerLayout(
            <RowLayout
                id='vnwLayout__row'
                cssString={rowCssString}
                align={align}
                justify={justify}
                ref={ref}
                {...restProps}
            >
                <LayoutContext.Provider value={props.current}>{children}</LayoutContext.Provider>
            </RowLayout>
        )
    }
)

export default Row
